import React, { useEffect, useRef } from 'react'
import SecondHeader from '../components/SecondHeader/SecondHeader'
import Footer from '../components/Footer/Footer'
import './aboutUs.css'

/* All copy below comes from the I-Sit Corp "About Us" source document. */

const SERVICE_CATEGORIES = [
    { icon: 'fa-dog', label: 'Dog walking and pet services' },
    { icon: 'fa-shoe-prints', label: 'Shoe cleaning and restoration' },
    { icon: 'fa-broom', label: 'Home cleaning and maintenance' },
    { icon: 'fa-car', label: 'Transportation and mobility services' },
    { icon: 'fa-box', label: 'Delivery and pickup services' },
    { icon: 'fa-spa', label: 'Beauty and personal-care services' },
    { icon: 'fa-hammer', label: 'Home repair and skilled trades' },
    { icon: 'fa-user-doctor', label: 'Healthcare and medical services' },
    { icon: 'fa-scale-balanced', label: 'Legal and professional services' },
    { icon: 'fa-briefcase', label: 'Business and consulting services' },
    { icon: 'fa-laptop-code', label: 'Technology and IT services' },
    { icon: 'fa-graduation-cap', label: 'Education and tutoring' },
    { icon: 'fa-cart-shopping', label: 'Shopping and personal assistance' },
    { icon: 'fa-champagne-glasses', label: 'Event and lifestyle services' },
];

const NEEDS = [
    'Your shoes need cleaning.',
    'Your dog needs to be walked.',
    'Your house needs cleaning.',
    'You need an appointment with a professional.',
    'You need something picked up and delivered.',
    'You need help with a project.',
];

const FLOW = [
    { icon: 'fa-magnifying-glass', label: 'Search' },
    { icon: 'fa-paper-plane', label: 'Request' },
    { icon: 'fa-calendar-check', label: 'Schedule' },
    { icon: 'fa-credit-card', label: 'Pay' },
    { icon: 'fa-mug-hot', label: 'Relax' },
];

const COMMITMENTS = [
    {
        icon: 'fa-bolt',
        title: 'Convenience',
        text: 'We believe your time is valuable. I-Sit is designed to help you accomplish more without having to spend your day running errands or searching for services.',
    },
    {
        icon: 'fa-sliders',
        title: 'Choice',
        text: 'People have different needs, preferences, budgets, and schedules. I-Sit is designed to provide access to a wide variety of services and professionals.',
    },
    {
        icon: 'fa-earth-americas',
        title: 'Accessibility',
        text: 'We want services to be easier to discover and access from virtually anywhere, whenever they are needed.',
    },
    {
        icon: 'fa-shield-halved',
        title: 'Trust',
        text: 'We are committed to creating a platform where customers and service providers can connect with confidence through clear information, service standards, and responsible technology.',
    },
    {
        icon: 'fa-lightbulb',
        title: 'Innovation',
        text: 'We are constantly looking for new ways to improve how people find, request, and receive services.',
    },
];

const AboutUs = () => {
    const heroVideoRef = useRef(null);

    /* The hero video loops purely as decoration, so honour reduced-motion:
       CSS can't stop media playback, this has to be done on the element. */
    useEffect(() => {
        const video = heroVideoRef.current;
        if (!video) return;

        const query = window.matchMedia('(prefers-reduced-motion: reduce)');
        const apply = () => {
            if (query.matches) {
                video.pause();
            } else {
                video.play().catch(() => { /* autoplay blocked — leave paused */ });
            }
        };

        apply();
        query.addEventListener?.('change', apply);
        return () => query.removeEventListener?.('change', apply);
    }, []);

    /* CRA is client-rendered, so the tab title / description are set here.
       NOTE: real SEO for this page needs SSR or prerendering before launch —
       crawlers that skip JS will still see the default CRA meta. */
    useEffect(() => {
        const prevTitle = document.title;
        const meta = document.querySelector('meta[name="description"]');
        const prevDescription = meta?.getAttribute('content');

        document.title = 'About Us — I-Sit Corp';
        meta?.setAttribute(
            'content',
            'I-Sit Corp is a technology-driven service platform connecting individuals, families, businesses, professionals, and service providers through one convenient digital experience.'
        );

        return () => {
            document.title = prevTitle;
            if (meta && prevDescription) meta.setAttribute('content', prevDescription);
        };
    }, []);

    return (
        <>
            <SecondHeader />

            <main className="about-page">
                {/* ============ HERO ============ */}
                <section className="au-hero">
                    <div className="container au-hero-inner">
                        <div className="row align-items-center">
                            <div className="col-lg-6">
                                <span className="au-badge">About Us — I-Sit Corp</span>
                                <h1>
                                    Your World of Services <br />
                                    <span className="au-hero-accent"> Right at Your Fingertips</span>
                                </h1>
                                <p className="au-hero-lead">
                                    At I-Sit Corp, we believe life should be easier. Why spend your
                                    valuable time searching, calling, traveling, waiting, and
                                    coordinating services when you can simply I-Sit and let us do
                                    the work?
                                </p>
                                <p className="au-hero-lead">
                                    I-Sit Corp is a technology-driven service platform designed to
                                    connect individuals, families, businesses, professionals, and
                                    service providers through one convenient digital experience.
                                    From everyday tasks to professional services, our goal is
                                    simple: bring the services you need directly to the palm of
                                    your hand.
                                </p>

                                <div className="au-hero-tagline">
                                    <span>You sit.</span>
                                    <i className="fa-solid fa-circle" aria-hidden="true"></i>
                                    <span>We connect.</span>
                                    <i className="fa-solid fa-circle" aria-hidden="true"></i>
                                    <span>Services come to you.</span>
                                </div>
                            </div>

                            <div className="col-lg-6">
                                {/* Hero artwork — decorative looping video (aria-hidden: the
                                    copy alongside carries the meaning). Swap the source path
                                    to change it; the container is square (1:1). */}
                                <div className="au-hero-art">
                                    <video
                                        ref={heroVideoRef}
                                        className="au-hero-art-video"
                                        src="/images/about-video.mp4"
                                        autoPlay
                                        muted
                                        loop
                                        playsInline
                                        preload="metadata"
                                        aria-hidden="true"
                                        tabIndex={-1}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ============ WHO WE ARE ============ */}
                <section className="au-section au-who">
                    <div className="container">
                        <div className="row align-items-center">
                            <div className="col-lg-6">
                                <span className="au-kicker">Who We Are</span>
                                <h2>
                                    A technology company built around one powerful concept:
                                    convenience without compromise
                                </h2>
                                <p>
                                    Our platform is designed to connect customers with a broad network
                                    of qualified professionals and service providers across multiple
                                    industries. Whether you need someone to walk your dog, clean your
                                    shoes, deliver an item, help with your home, provide professional
                                    advice, or connect you with a doctor or lawyer, I-Sit is designed
                                    to make finding and accessing that service easier.
                                </p>
                                <p>
                                    We are creating an ecosystem where services, professionals,
                                    businesses, and customers come together in one place.
                                </p>
                            </div>

                            <div className="col-lg-6">
                                <blockquote className="au-quote">
                                    <i className="fa-solid fa-quote-left" aria-hidden="true"></i>
                                    <p>I-Sit is a technology company focused on services.</p>
                                </blockquote>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ============ WHAT WE DO ============ */}
                <section className="au-section au-what">
                    <div className="container">
                        <div className="au-section-head">
                            <span className="au-kicker">What We Do</span>
                            <h2>Everything you need, without searching everywhere</h2>
                            <p>
                                I-Sit Corp provides a technology platform that helps customers
                                discover, request, schedule, and access services from the convenience
                                of their home, office, or wherever they are. Our vision is to create a
                                platform where customers can find virtually everything they need
                                without having to search across multiple websites, applications, phone
                                numbers, or businesses.
                            </p>
                        </div>

                        <p className="au-what-intro">
                            Our service categories can include everything from everyday personal tasks
                            to highly specialized professional services, such as:
                        </p>

                        <div className="au-cat-grid">
                            {SERVICE_CATEGORIES.map((cat) => (
                                <div className="au-cat-card" key={cat.label}>
                                    <span className="au-cat-icon">
                                        <i className={`fa-solid ${cat.icon}`} aria-hidden="true"></i>
                                    </span>
                                    <span className="au-cat-label">{cat.label}</span>
                                </div>
                            ))}
                            <div className="au-cat-card au-cat-card-more">
                                <span className="au-cat-icon">
                                    <i className="fa-solid fa-ellipsis" aria-hidden="true"></i>
                                </span>
                                <span className="au-cat-label">And much more</span>
                            </div>
                        </div>

                        <p className="au-cat-note">
                            The possibilities are virtually limitless because I-Sit is built around
                            services, not a single product or industry.
                        </p>
                    </div>
                </section>

                {/* ============ THE I-SIT EXPERIENCE ============ */}
                <section className="au-section au-experience">
                    <div className="container">
                        <div className="row align-items-start">
                            <div className="col-lg-5">
                                <span className="au-kicker">The I-Sit Experience</span>
                                <h2>Imagine waking up and realizing you need several things taken care of.</h2>
                                <ul className="au-needs">
                                    {NEEDS.map((need) => (
                                        <li key={need}>
                                            <i className="fa-regular fa-circle-check" aria-hidden="true"></i>
                                            {need}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="col-lg-7">
                                <div className="au-flow-card">
                                    <p className="au-flow-intro">
                                        Instead of spending hours searching for different companies and
                                        service providers, you open I-Sit.
                                    </p>

                                    <ol className="au-flow">
                                        {FLOW.map((step, index) => (
                                            <li key={step.label}>
                                                <span className="au-flow-icon">
                                                    <i className={`fa-solid ${step.icon}`} aria-hidden="true"></i>
                                                </span>
                                                <span className="au-flow-label">{step.label}</span>
                                                {index < FLOW.length - 1 && (
                                                    <i
                                                        className="fa-solid fa-chevron-right au-flow-arrow"
                                                        aria-hidden="true"
                                                    ></i>
                                                )}
                                            </li>
                                        ))}
                                    </ol>

                                    <p className="au-flow-out">You don't have to move your finger.</p>
                                </div>

                                <p className="au-mission-line">
                                    <i className="fa-solid fa-bullseye" aria-hidden="true"></i>
                                    Our mission is to make everyday life more convenient by putting the
                                    services people need directly at their fingertips.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ============ OUR MARKETPLACE ============ */}
                <section className="au-section au-marketplace">
                    <div className="container">
                        <div className="au-section-head au-section-head-center">
                            <span className="au-kicker">Our Marketplace</span>
                            <h2>I-Sit brings together two essential sides of the service economy</h2>
                        </div>

                        <div className="row au-market-row">
                            <div className="col-md-6">
                                <article className="au-market-card">
                                    <span className="au-market-icon">
                                        <i className="fa-solid fa-user" aria-hidden="true"></i>
                                    </span>
                                    <h3>Customers</h3>
                                    <p>
                                        Customers can use I-Sit to discover and access services in one
                                        convenient platform. Our goal is to save people time, reduce
                                        frustration, and make it easier to get things done.
                                    </p>
                                </article>
                            </div>

                            <div className="col-md-6">
                                <article className="au-market-card au-market-card-alt">
                                    <span className="au-market-icon">
                                        <i className="fa-solid fa-handshake" aria-hidden="true"></i>
                                    </span>
                                    <h3>Professionals &amp; Service Providers</h3>
                                    <p>
                                        Professionals and businesses can use I-Sit as a technology
                                        platform to showcase their services, connect with customers,
                                        manage opportunities, and grow their businesses. From an
                                        independent dog walker to a major professional practice, I-Sit is
                                        designed to create opportunities across a wide range of industries.
                                    </p>
                                </article>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ============ OUR TECHNOLOGY ============ */}
                <section className="au-technology">
                    <div className="container">
                        <div className="row align-items-center">
                            <div className="col-lg-7">
                                <span className="au-kicker au-kicker-light">Our Technology</span>
                                <h2>Technology is at the center of everything we do</h2>
                                <p>
                                    I-Sit is designed to make the process of finding and accessing
                                    services simple, fast, convenient, and transparent. Our platform
                                    brings together customers and service providers while creating
                                    seamless digital experience for discovering, requesting, scheduling,
                                    and managing services.
                                </p>
                                <p>
                                    As technology continues to evolve, I-Sit will continue to explore new
                                    ways to use technology, automation, artificial intelligence,
                                    location-based services, digital payments, and other innovations to
                                    make accessing services easier than ever.
                                </p>
                            </div>

                            <div className="col-lg-5">
                                <div className="au-tech-tags">
                                    <span><i className="fa-solid fa-robot" aria-hidden="true"></i> Automation</span>
                                    <span><i className="fa-solid fa-brain" aria-hidden="true"></i> Artificial intelligence</span>
                                    <span><i className="fa-solid fa-location-dot" aria-hidden="true"></i> Location-based services</span>
                                    <span><i className="fa-solid fa-credit-card" aria-hidden="true"></i> Digital payments</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ============ OUR COMMITMENT ============ */}
                <section className="au-section au-commitment">
                    <div className="container">
                        <div className="au-section-head au-section-head-center">
                            <span className="au-kicker">Our Commitment</span>
                            <h2>What we hold ourselves to</h2>
                        </div>

                        <div className="au-commit-grid">
                            {COMMITMENTS.map((item) => (
                                <article className="au-commit-card" key={item.title}>
                                    <span className="au-commit-icon">
                                        <i className={`fa-solid ${item.icon}`} aria-hidden="true"></i>
                                    </span>
                                    <h3>{item.title}</h3>
                                    <p>{item.text}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ============ WHY CHOOSE I-SIT ============ */}
                <section className="">
                    <div className="container">
                        <div className="au-why-inner">
                            <span className="au-kicker">Why Choose I-Sit?</span>
                            <h2>Because life is busy.</h2>
                            <p>
                                You shouldn't have to spend hours searching for the right person to do
                                the job. I-Sit is designed to become your one-stop digital destination
                                for services — a place where you can find the people, professionals,
                                businesses, and solutions you need.
                            </p>

                            <div className="au-ask">
                                <div className="au-ask-item au-ask-before">
                                    <span className="au-ask-label">Instead of asking</span>
                                    <p>“Who can do this for me?”</p>
                                </div>
                                <i className="fa-solid fa-arrow-right au-ask-arrow" aria-hidden="true"></i>
                                <div className="au-ask-item au-ask-after">
                                    <span className="au-ask-label">You can simply ask</span>
                                    <p>“Can I get it through I-Sit?”</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ============ VISION & MISSION ============ */}
                <section className="au-section au-vm">
                    <div className="container">
                        <div className="row au-vm-row">
                            <div className="col-md-6">
                                <article className="au-vm-card">
                                    <span className="au-vm-icon">
                                        <i className="fa-regular fa-eye" aria-hidden="true"></i>
                                    </span>
                                    <span className="au-kicker">Our Vision</span>
                                    <p>
                                        Our vision is to build one of the world's leading technology
                                        platforms for connecting people with services.
                                    </p>
                                    <p>
                                        We envision a future where accessing a service is as simple as
                                        opening an app, selecting what you need, and letting technology
                                        connect you with the right provider. From the simplest everyday
                                        task to complex professional services, I-Sit wants to be the
                                        platform people think of first.
                                    </p>
                                </article>
                            </div>

                            <div className="col-md-6">
                                <article className="au-vm-card">
                                    <span className="au-vm-icon">
                                        <i className="fa-solid fa-flag" aria-hidden="true"></i>
                                    </span>
                                    <span className="au-kicker">Our Mission</span>
                                    <p>
                                        To simplify everyday life by connecting people with the services
                                        they need through one convenient, innovative technology platform.
                                    </p>
                                    <p>
                                        We are building an ecosystem where customers can sit back, relax,
                                        and let I-Sit help get things done.
                                    </p>
                                </article>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ============ THE I-SIT PROMISE ============ */}
                <section className="au-promise">
                    <span className="au-hero-glow au-hero-glow-3" aria-hidden="true"></span>
                    <div className="container au-promise-inner">
                        <span className="au-kicker au-kicker-light">The I-Sit Promise</span>
                        <h2>You Sit. We Handle the Rest.</h2>
                        <p>
                            Whether you need your shoes cleaned, your dog walked, your home serviced,
                            transportation arranged, a professional consulted, or a specialized
                            service delivered, I-Sit is designed to put the solution in the palm of
                            your hand.
                        </p>
                        <p className="au-promise-sign">
                            I-Sit Corp More Than a Service. A Smarter Way to Live.
                        </p>
                    </div>
                </section>
            </main>

            <Footer />
        </>
    )
}

export default AboutUs
